import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = process.env.REACT_APP_STORAGE_ENCRYPTION_KEY || "default-fallback-key-change-this";

/**
 * Secure storage utility that encrypts data before storing
 * Uses sessionStorage by default for better security
 */
class SecureStorage {
  constructor(useSessionStorage = true) {
    this.storage = useSessionStorage ? sessionStorage : localStorage;
  }

  /**
   * Encrypt and store data
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be stringified)
   */
  setItem(key, value) {
    try {
      const jsonValue = JSON.stringify(value);
      const encrypted = CryptoJS.AES.encrypt(jsonValue, ENCRYPTION_KEY).toString();
      this.storage.setItem(key, encrypted);
      return true;
    } catch (error) {
      console.error("SecureStorage: Error setting item", error);
      return false;
    }
  }

  /**
   * Retrieve and decrypt data
   * @param {string} key - Storage key
   * @returns {any} Decrypted value or null if not found/error
   */
  getItem(key) {
    try {
      const encrypted = this.storage.getItem(key);
      if (!encrypted) return null;

      const bytes = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      if (!decrypted) return null;

      return JSON.parse(decrypted);
    } catch (error) {
      console.error("SecureStorage: Error getting item", error);
      return null;
    }
  }

  /**
   * Remove item from storage
   * @param {string} key - Storage key
   */
  removeItem(key) {
    try {
      this.storage.removeItem(key);
      return true;
    } catch (error) {
      console.error("SecureStorage: Error removing item", error);
      return false;
    }
  }

  /**
   * Clear all items from storage
   */
  clear() {
    try {
      this.storage.clear();
      return true;
    } catch (error) {
      console.error("SecureStorage: Error clearing storage", error);
      return false;
    }
  }

  /**
   * Get all keys in storage
   * @returns {string[]} Array of keys
   */
  getAllKeys() {
    try {
      return Object.keys(this.storage);
    } catch (error) {
      console.error("SecureStorage: Error getting keys", error);
      return [];
    }
  }
}

// Export singleton instances
export const secureSessionStorage = new SecureStorage(true);
export const secureLocalStorage = new SecureStorage(false);

export default secureSessionStorage;
